import { Vector2, Vector3 } from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { PlaygroundVertex } from './PlaygroundVertex';
import { ProjectRenderer } from '../ProjectRenderer';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { PlaygroundManager } from '../managers/PlaygroundManager';
import { clamp, debounce } from 'lodash';
import { RenderUtils } from '../utils/RenderUtils';
import { PlaygroundShapeType } from '@/types/creator';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const COLORS = RENDERER_CONFIG.COLORS;

const { PLAYGROUND_LINE } = RENDERER_CONFIG;

export const lineMaterial = new LineMaterial({
	color: COLORS.BLACK,
	linewidth: PLAYGROUND_LINE.WIDTH,
});
const dashedLineMaterial = new LineMaterial({
	color: COLORS.BLACK,
	linewidth: PLAYGROUND_LINE.WIDTH,
	dashed: true,
});

export const LINE_POS_Y = PLAYGROUND_LINE.HEIGHT / 2;

export class PlaygroundLine {
	private line: Line2;
	private geometry = new LineGeometry();
	private material: LineMaterial;
	private _isError = false;
	private popover!: HTMLDivElement;
	input!: HTMLInputElement;
	css2dObject!: CSS2DObject;
	start: PlaygroundVertex;
	end: PlaygroundVertex;
	position: Vector2;

	constructor(
		private renderer: ProjectRenderer,
		private manager: PlaygroundManager,
		start: PlaygroundVertex,
		end: PlaygroundVertex
	) {
		this.start = start;
		this.end = end;

		this.position = new Vector2()
			.addVectors(start.position, end.position)
			.multiplyScalar(0.5);

		this.material = end.isMovable ? dashedLineMaterial.clone() : lineMaterial;
		this.line = new Line2(this.geometry, this.material);
		this.renderer.scene.add(this.line);

		this.initPopover();
		this.addEventListeners();
		this.compute();
	}

	get isError() {
		return this._isError;
	}

	set isError(value: boolean) {
		this._isError = value;
		this.end.isError = value;
		this.material.color.set(value ? COLORS.RED : COLORS.BLACK);
	}

	private initPopover() {
		this.popover = document.createElement('div');
		this.popover.className = 'renderer-popover';

		this.input = document.createElement('input');
		this.input.className = 'renderer-input';
		this.input.inputMode = 'numeric';

		this.input.min = RENDERER_CONFIG.LINE_MIN_LENGTH.toString();
		this.input.max = RENDERER_CONFIG.LINE_MAX_LENGTH.toString();
		this.input.step = '1';

		this.popover.appendChild(this.input);

		this.css2dObject = new CSS2DObject(this.popover);
		this.css2dObject.position.set(this.position.x, 0.5, this.position.y);
		this.renderer.scene.add(this.css2dObject);
	}

	private addEventListeners() {
		this.input.addEventListener(
			'input',
			debounce(({ target }) => {
				const sanitized = target.value
					.replace(',', '.')
					.replace(/[^0-9.]/g, '');
				const value = Number(sanitized);

				const length = clamp(
					value,
					RENDERER_CONFIG.LINE_MIN_LENGTH,
					RENDERER_CONFIG.LINE_MAX_LENGTH
				);

				this.input.value = `${Math.round(length * 100) / 100} m`;
				this.handleResize(length);
			}, 1000)
		);
	}

	private doesIntersectWith(lineA: PlaygroundLine, lineB: PlaygroundLine) {
		const p1 = lineA.start.position;
		const p2 = lineA.end.position;
		const q1 = lineB.start.position;
		const q2 = lineB.end.position;

		const intersect =
			RenderUtils.ccw(p1, q1, q2) !== RenderUtils.ccw(p2, q1, q2) &&
			RenderUtils.ccw(p1, p2, q1) !== RenderUtils.ccw(p1, p2, q2);

		if (intersect) {
			const sharedPoints = [
				p1.equals(q1),
				p1.equals(q2),
				p2.equals(q1),
				p2.equals(q2),
			];
			if (sharedPoints.includes(true)) return false;
		}

		return intersect;
	}

	private handleResizeRectangle(newLength: number) {
		const currentLength = this.start.position.distanceTo(this.end.position);
		if (currentLength === 0 || newLength === currentLength) return;

		const lineDirection = new Vector2()
			.subVectors(this.end.position, this.start.position)
			.normalize();

		const parallelStart = this.start.prev;
		const parallelEnd = this.end.next;

		const lengthDifference = newLength - currentLength;

		const endOffset = lineDirection
			.clone()
			.multiplyScalar(lengthDifference / 2);

		this.end.position = this.end.position.clone().add(endOffset);
		if (parallelEnd)
			parallelEnd.position = parallelEnd.position.clone().add(endOffset);

		this.start.position = this.start.position.clone().sub(endOffset);
		if (parallelStart)
			parallelStart.position = parallelStart.position.clone().sub(endOffset);
	}

	private handleResizeCustomShape(newLength: number, secondCheck = false) {
		const currentLength = this.start.position.distanceTo(this.end.position);
		if (currentLength === 0 || newLength === currentLength) return;

		const originalStartPos = this.start.position.clone(),
			originalEndPos = this.end.position.clone();

		const lineDirection = new Vector2();

		lineDirection.subVectors(this.end.position, this.start.position);
		if (secondCheck) lineDirection.negate();

		lineDirection.normalize();

		const lengthDifference = newLength - currentLength;

		const offset = lineDirection.clone().multiplyScalar(lengthDifference);

		if (secondCheck)
			this.start.position = this.start.position.clone().add(offset);
		else this.end.position = this.end.position.clone().add(offset);

		this.checkIntersectionsWithOthers(this.manager.lines);

		if (this.isError && secondCheck) {
			this.start.position = originalStartPos.clone();

			this.isError = false;

			this.input.classList.add('error');
			setTimeout(() => {
				this.input.classList.remove('error');
			}, 3000);

			return;
		}

		if (this.isError) {
			this.end.position = originalEndPos.clone();
			this.isError = false;
			this.handleResizeCustomShape(newLength, true);
			return;
		}
	}

	checkIntersectionsWithOthers(others: PlaygroundLine[]) {
		for (const other of others) {
			if (other === this) continue;
			if (this.doesIntersectWith(this, other)) {
				this.isError = true;
				return;
			}
		}
		this.isError = false;
	}

	handleResize(newLength: number) {
		switch (this.manager.shapeType) {
			case PlaygroundShapeType.RECTANGLE:
				this.handleResizeRectangle(newLength);
				break;
			case PlaygroundShapeType.CUSTOM:
				this.handleResizeCustomShape(newLength);
				break;
		}
	}

	compute() {
		this.geometry.setFromPoints([
			new Vector3(this.start.position.x, LINE_POS_Y, this.start.position.y),
			new Vector3(this.end.position.x, LINE_POS_Y, this.end.position.y),
		]);
		this.position = new Vector2()
			.addVectors(this.start.position, this.end.position)
			.multiplyScalar(0.5);

		this.line.computeLineDistances();

		const length = this.start.position.distanceTo(this.end.position);
		this.input.value = `${Math.round(length * 100) / 100} m`;

		const direction = new Vector2()
			.subVectors(this.end.position, this.start.position)
			.normalize();

		const perpendicular = new Vector2(-direction.y, direction.x);

		const labelPos = new Vector2().addVectors(
			this.position,
			perpendicular.multiplyScalar(PLAYGROUND_LINE.LABEL_TO_LINE_OFFSET)
		);

		this.css2dObject.position.set(labelPos.x, 0.5, labelPos.y);
	}

	updateMaterial() {
		this.material = this.end.isMovable
			? dashedLineMaterial.clone()
			: lineMaterial;
		this.line.material = this.material;
	}

	dispose() {
		this.css2dObject.element.remove();
		RenderUtils.disposeObject(this.line);
		RenderUtils.disposeObject(this.css2dObject);
	}
}
